import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useSession } from "../../context/AuthContext";
import api from "../../services/api";

interface NextLearningStepProps {
  stats?: any;
  loading?: boolean;
}

export default function NextLearningStep({ stats }: NextLearningStepProps) {
  const { data: session } = useSession();
  const userId = session?.user?.id;
  const userName = session?.user?.name || "Student";
  const firstName = userName.split(' ')[0];
  const membership = (session?.user as any)?.highest_plan_name || (session?.user as any)?.membership || "FREE";
  const userPoints = stats?.points || 0;
  const badges = stats?.badges || [];
  const latestBadge = badges[0];
  
  const [data, setData] = useState<any>(null);
  const [progress, setProgress] = useState<number>(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (!userId) return;
      try {
        const [res, progressRes] = await Promise.all([
          api.get(`/dashboard/resume/${userId}`),
          api.get(`/progress/${userId}`)
        ]);
        const lastPlayed = res.data?.lastPlayed;
        setData(lastPlayed);
        if (lastPlayed && progressRes.data && progressRes.data[lastPlayed.id] !== undefined) {
          setProgress(progressRes.data[lastPlayed.id]);
        }
      } catch (err) {
        console.error("Failed to load next learning step", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [userId]);

  if (loading) {
    return (
      <div className="bg-surface-container-lowest border border-outline-variant/10 rounded-[2rem] p-8 animate-pulse h-64 w-full">
        <div className="w-1/3 h-8 bg-surface-container-high rounded-xl mb-4" />
        <div className="w-1/2 h-6 bg-surface-container-high rounded-xl" />
      </div>
    );
  }

  if (!data) {
    return null; // No courses available to suggest
  }

  const { title, isStartHere, isAllCompleted, missionNumber, potentialXp, currentXp, id } = data;

  if (isAllCompleted) {
    return (
      <div className="bg-gradient-to-br from-[#961464] to-[#500a6e] p-6 sm:p-10 rounded-[2.5rem] text-white shadow-2xl relative overflow-hidden group border border-white/10">
        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-fuchsia-400/20 rounded-full blur-[80px] -mr-20 -mt-20 animate-pulse" />
        
        <div className="relative z-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-8">
          <div className="flex-1 space-y-6">
            <div>
              <div className="mb-6 space-y-3">
                <div>
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/20 rounded-lg text-xs font-black uppercase tracking-widest text-white border border-white/20 shadow-sm">
                    🔥 You're All Caught Up!
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-1.5 px-3 py-1.5 bg-black/10 text-white rounded-lg border border-white/20 backdrop-blur-sm">
                    <span className="material-symbols-outlined text-sm font-bold">military_tech</span>
                    <span className="text-[10px] font-bold uppercase tracking-wider">{membership} RANK</span>
                  </div>
                  <div className="flex items-center gap-1.5 px-3 py-1.5 bg-black/10 text-white rounded-lg border border-white/20 backdrop-blur-sm">
                    <span className="material-symbols-outlined text-sm font-bold">local_fire_department</span>
                    <span className="text-[10px] font-bold uppercase tracking-wider">0 DAY STREAK</span>
                  </div>
                </div>
              </div>
              <h2 className="text-3xl sm:text-4xl font-black tracking-tight leading-tight mb-2 drop-shadow-md">
                You're all caught up, {firstName}!
              </h2>
              <p className="text-fuchsia-100/80 font-medium text-lg">
                You've completed all currently available missions. Fantastic work!
              </p>
            </div>
            
            <div className="bg-white/5 border border-white/10 rounded-2xl p-5 backdrop-blur-md max-w-xl shadow-lg">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">⏳</span>
                  <h3 className="font-bold text-lg text-white">Awaiting Next Drop</h3>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-[10px] text-fuchsia-200 font-bold uppercase tracking-widest mb-1">Missions Conquered</p>
                  <p className="font-black text-white text-lg">{missionNumber - 1}</p>
                </div>
                <div>
                <p className="text-[10px] text-fuchsia-200 font-bold uppercase tracking-widest mb-1">⭐ Total XP</p>
                  <p className="font-black text-white text-lg">{currentXp || 0}</p>
                </div>
              </div>
                
              <div className="mt-4 pt-4 border-t border-white/10 flex items-center justify-between">
                <div>
                  <p className="text-[10px] text-fuchsia-200 font-bold uppercase tracking-widest mb-1">Your Points</p>
                  <p className="font-black text-white text-xl">{userPoints.toLocaleString()}</p>
                </div>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${latestBadge ? 'bg-fuchsia-400 text-white' : 'bg-white/10 text-fuchsia-200'}`}>
                    <span className="material-symbols-outlined text-xl">{latestBadge?.icon || "lock"}</span>
                  </div>
                  <div className="text-left hidden sm:block">
                    <p className="text-[10px] text-fuchsia-200 font-bold uppercase tracking-widest mb-0.5">Current Business Status</p>
                    <p className="font-bold text-sm text-white">{latestBadge?.name || "No Status Yet"}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-black/20 p-6 rounded-3xl border border-white/5 md:min-w-[280px] backdrop-blur-sm shadow-inner">
            <p className="text-xs font-black uppercase tracking-widest text-fuchsia-200 mb-4 flex items-center gap-2">
              <span className="material-symbols-outlined text-[16px]">stars</span>
              What's Next?
            </p>
            <ul className="space-y-3 mb-8">
              <li className="flex items-center gap-3 text-sm font-medium text-fuchsia-100">
                <span className="material-symbols-outlined text-fuchsia-400 text-[18px]">verified</span>
                Review past missions
              </li>
              <li className="flex items-center gap-3 text-sm font-medium text-fuchsia-100">
                <span className="material-symbols-outlined text-fuchsia-400 text-[18px]">update</span>
                Wait for new releases
              </li>
              <li className="flex items-center gap-3 text-sm font-medium text-fuchsia-100">
                <span className="material-symbols-outlined text-fuchsia-400 text-[18px]">workspace_premium</span>
                Maintain your rank
              </li>
            </ul>
            
            <Link 
              to="/courses"
              className="w-full bg-white text-fuchsia-950 hover:bg-fuchsia-50 px-6 py-4 rounded-xl font-black text-[11px] uppercase tracking-[0.15em] flex items-center justify-center gap-2 transition-all hover:scale-105 active:scale-95 shadow-xl shadow-white/20"
            >
              <span className="material-symbols-outlined text-[18px]">library_books</span>
              Review Past Missions
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (isStartHere) {
    // New Student Design
    return (
      <div className="bg-gradient-to-br from-cyan-900 to-cyan-950 p-6 sm:p-10 rounded-[2.5rem] text-white shadow-2xl relative overflow-hidden group">
        <div className="absolute top-0 right-0 w-64 h-64 bg-cyan-500/10 rounded-full blur-3xl -mr-20 -mt-20 group-hover:scale-110 transition-transform duration-700" />
        
        <div className="relative z-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-8">
          <div className="flex-1 space-y-6">
            <div>
              <h2 className="text-3xl sm:text-4xl font-black tracking-tight leading-tight mb-2">
                Welcome to your learning journey, {firstName}!
              </h2>
              <p className="text-cyan-200/80 font-medium text-lg">
                Mission 1 is ready.
              </p>
            </div>
          </div>
        
          <div className="md:min-w-[280px]">
            <Link 
              to={`/courses/${id}`}
              className="w-full bg-cyan-500 text-white hover:bg-cyan-400 px-4 sm:px-8 py-3 sm:py-5 rounded-xl font-black text-xs sm:text-sm uppercase tracking-widest sm:tracking-[0.15em] flex items-center justify-center gap-2 transition-all hover:scale-105 active:scale-95 shadow-xl shadow-cyan-500/20"
            >
              <span className="material-symbols-outlined text-[20px]">play_circle</span>
              Start Mission
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Returning Student Design
  return (
    <div className="bg-gradient-to-br from-[#961464] to-[#500a6e] p-6 sm:p-10 rounded-[2.5rem] text-white shadow-2xl relative overflow-hidden group border border-white/10">
      <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-fuchsia-400/20 rounded-full blur-[80px] -ml-40 -mt-40 group-hover:bg-fuchsia-400/30 transition-colors duration-1000" />
      
      <div className="relative z-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-8">
        <div className="flex-1 space-y-4 min-w-0 w-full">
          <p className="text-fuchsia-100/80 font-bold uppercase tracking-widest text-xs">
            Welcome back, {firstName}!
          </p>
          <h2 className="text-xl sm:text-2xl font-bold tracking-tight leading-snug break-words whitespace-normal min-w-0">
            {title}
          </h2>
          <div className="inline-flex items-center gap-2 bg-white/10 px-4 py-2 rounded-xl text-fuchsia-100 font-bold text-sm">
            <span className="text-lg">🏆</span>
            +{potentialXp || 100} XP Reward
          </div>
        </div>
        
        <div className="md:min-w-[280px]">
          {progress > 0 && (
            <div className="mb-4">
              <div className="flex justify-between items-center text-[10px] font-black uppercase text-fuchsia-200 tracking-widest mb-2">
                <span>Mission Progress</span>
                <span className="text-emerald-400">{progress}%</span>
              </div>
              <div className="h-1.5 w-full bg-black/40 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-emerald-400 shadow-[0_0_10px_rgba(52,211,153,0.3)] transition-all duration-1000" 
                  style={{ width: `${progress}%` }} 
                />
              </div>
            </div>
          )}
          
          <Link 
            to={`/courses/${id}`}
            className="w-full bg-white text-fuchsia-950 hover:bg-fuchsia-50 px-4 sm:px-8 py-3 sm:py-5 rounded-xl font-black text-xs sm:text-sm uppercase tracking-widest sm:tracking-[0.15em] flex items-center justify-center gap-2 transition-all hover:scale-105 active:scale-95 shadow-xl shadow-white/20"
          >
            <span className="material-symbols-outlined text-[20px]">play_circle</span>
            {progress === 0 ? "Start Mission" : "Continue Mission"}
          </Link>
        </div>
      </div>
    </div>
  );
}
